import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTE_NAMES } from '../../routes/routeConstants';
import { useAuth } from '../../provider/AuthProvider';
import { useAppStore } from '../../store/useAppStore';
import { api } from '../../utils/api';
import { Download, Trash2, Eye, EyeOff } from 'lucide-react';

const UserProfilePage: React.FC = () => {
  const { user, logout } = useAuth();
  const { addToast } = useAppStore();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [profileVisible, setProfileVisible] = useState(true);

  const [profileData, setProfileData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phoneNumber || '',
    location: '',
    title: '',
    bio: '',
    skills: [] as string[],
    experience: '',
    education: '',
    linkedin: '',
    website: ''
  });

  const handleSave = () => {
    // Save profile data
    setIsEditing(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setProfileData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: '👤' },
    { id: 'applications', label: 'Applications', icon: '📋' },
    { id: 'saved', label: 'Saved Jobs', icon: '💾' },
    { id: 'settings', label: 'Settings', icon: '⚙️' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-white rounded-2xl p-8 mb-6 shadow-sm">
          <div className="flex items-start gap-6">
            <div className="w-24 h-24 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
              {profileData.firstName[0]}{profileData.lastName[0]}
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--twc-ink)' }}>
                {profileData.firstName} {profileData.lastName}
              </h1>
              <p className="text-lg text-gray-600 mb-2">{profileData.title}</p>
              <p className="text-gray-500">{profileData.location}</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setIsEditing(!isEditing)}
                className="px-4 py-2 rounded-lg border font-medium transition-all hover:opacity-80"
                style={{ borderColor: 'var(--twc-timberwolf)', color: 'var(--twc-feldgrau)' }}
              >
                {isEditing ? 'Cancel' : 'Edit Profile'}
              </button>
              {isEditing && (
                <button
                  onClick={handleSave}
                  className="px-4 py-2 rounded-lg text-white font-medium transition-all hover:opacity-90"
                  style={{ backgroundColor: 'var(--twc-tiger)' }}
                >
                  Save Changes
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <nav className="space-y-2">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-all ${
                      activeTab === tab.id 
                        ? 'bg-blue-50 text-blue-700' 
                        : 'hover:bg-gray-50 text-gray-600'
                    }`}
                  >
                    <span className="text-lg">{tab.icon}</span>
                    <span className="font-medium">{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {activeTab === 'profile' && (
              <div className="bg-white rounded-2xl p-8 shadow-sm">
                <h2 className="text-2xl font-semibold mb-6" style={{ color: 'var(--twc-ink)' }}>
                  Profile Information
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--twc-feldgrau)' }}>
                      First Name
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={profileData.firstName}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cambridge disabled:bg-gray-50"
                      style={{ backgroundColor: isEditing ? '#F9FAFB' : 'white' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--twc-feldgrau)' }}>
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={profileData.lastName}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cambridge disabled:bg-gray-50"
                      style={{ backgroundColor: isEditing ? '#F9FAFB' : 'white' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--twc-feldgrau)' }}>
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={profileData.email}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cambridge disabled:bg-gray-50"
                      style={{ backgroundColor: isEditing ? '#F9FAFB' : 'white' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--twc-feldgrau)' }}>
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={profileData.phone}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cambridge disabled:bg-gray-50"
                      style={{ backgroundColor: isEditing ? '#F9FAFB' : 'white' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--twc-feldgrau)' }}>
                      Location
                    </label>
                    <input
                      type="text"
                      name="location"
                      value={profileData.location}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cambridge disabled:bg-gray-50"
                      style={{ backgroundColor: isEditing ? '#F9FAFB' : 'white' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--twc-feldgrau)' }}>
                      Professional Title
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={profileData.title}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cambridge disabled:bg-gray-50"
                      style={{ backgroundColor: isEditing ? '#F9FAFB' : 'white' }}
                    />
                  </div>
                </div>
                <div className="mt-6">
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--twc-feldgrau)' }}>
                    Bio
                  </label>
                  <textarea
                    name="bio"
                    value={profileData.bio}
                    onChange={handleChange}
                    disabled={!isEditing}
                    rows={4}
                    className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cambridge disabled:bg-gray-50"
                    style={{ backgroundColor: isEditing ? '#F9FAFB' : 'white' }}
                  />
                </div>
              </div>
            )}

            {activeTab === 'applications' && (
              <div className="bg-white rounded-2xl p-8 shadow-sm">
                <h2 className="text-2xl font-semibold mb-6" style={{ color: 'var(--twc-ink)' }}>
                  My Applications
                </h2>
                <div className="text-center py-8 text-gray-500">
                  <p className="mb-4">View and track your applications from the dashboard.</p>
                  <button
                    onClick={() => navigate(ROUTE_NAMES.PAGES.MY_APPLICATIONS)}
                    className="px-4 py-2 rounded-lg text-white font-medium transition-all hover:opacity-90"
                    style={{ backgroundColor: 'var(--twc-tiger)' }}
                  >
                    Go to My Applications
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'saved' && (
              <div className="bg-white rounded-2xl p-8 shadow-sm">
                <h2 className="text-2xl font-semibold mb-6" style={{ color: 'var(--twc-ink)' }}>
                  Saved Jobs
                </h2>
                <div className="text-center py-8 text-gray-500">
                  <p className="mb-4">Browse jobs and save them by clicking the heart icon.</p>
                  <button
                    onClick={() => navigate(ROUTE_NAMES.PAGES.JOBS)}
                    className="px-4 py-2 rounded-lg text-white font-medium transition-all hover:opacity-90"
                    style={{ backgroundColor: 'var(--twc-tiger)' }}
                  >
                    Browse Jobs
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'settings' && (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl p-8 shadow-sm">
                  <h2 className="text-2xl font-semibold mb-6" style={{ color: 'var(--twc-ink)' }}>
                    Account Settings
                  </h2>
                  <div className="space-y-3">
                    <label className="flex items-center gap-3">
                      <input type="checkbox" defaultChecked className="rounded" />
                      <span>Job alerts</span>
                    </label>
                    <label className="flex items-center gap-3">
                      <input type="checkbox" defaultChecked className="rounded" />
                      <span>Application updates</span>
                    </label>
                    <label className="flex items-center gap-3">
                      <input type="checkbox" className="rounded" />
                      <span>Marketing emails</span>
                    </label>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-8 shadow-sm">
                  <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--twc-ink)' }}>
                    Privacy & Data
                  </h3>

                  {/* Profile Visibility Toggle */}
                  <div className="flex items-center justify-between py-4 border-b"
                       style={{ borderColor: 'var(--twc-timberwolf)' }}>
                    <div className="flex items-center gap-3">
                      {profileVisible ?
                        <Eye className="w-5 h-5" style={{ color: 'var(--twc-cambridge)' }} /> :
                        <EyeOff className="w-5 h-5 text-gray-400" />
                      }
                      <div>
                        <p className="font-medium" style={{ color: 'var(--twc-ink)' }}>Profile Visibility</p>
                        <p className="text-sm text-gray-500">
                          {profileVisible
                            ? 'Your profile is visible to employers'
                            : 'Your profile is hidden from employer searches'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          const newValue = !profileVisible;
                          await api.put(`/auth/updateUser/${user?.id}`, { profileVisible: newValue });
                          setProfileVisible(newValue);
                          addToast({
                            type: 'success',
                            title: 'Profile Updated',
                            message: newValue ? 'Your profile is now visible.' : 'Your profile is now hidden from searches.',
                          });
                        } catch {
                          addToast({ type: 'error', title: 'Update Failed', message: 'Failed to update visibility.' });
                        }
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        profileVisible ? '' : 'bg-gray-300'
                      }`}
                      style={profileVisible ? { backgroundColor: 'var(--twc-cambridge)' } : {}}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        profileVisible ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>

                  {/* Export Data */}
                  <div className="flex items-center justify-between py-4 border-b"
                       style={{ borderColor: 'var(--twc-timberwolf)' }}>
                    <div className="flex items-center gap-3">
                      <Download className="w-5 h-5" style={{ color: 'var(--twc-cambridge)' }} />
                      <div>
                        <p className="font-medium" style={{ color: 'var(--twc-ink)' }}>Export My Data</p>
                        <p className="text-sm text-gray-500">Download all your personal data as a JSON file</p>
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          setIsExporting(true);
                          const response = await api.get('/auth/export-data');
                          const data = await response.json();
                          const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                          const url = URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `acanetic-data-export-${new Date().toISOString().slice(0, 10)}.json`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          URL.revokeObjectURL(url);
                          addToast({ type: 'success', title: 'Export Complete', message: 'Your data has been downloaded.' });
                        } catch {
                          addToast({ type: 'error', title: 'Export Failed', message: 'Failed to export data. Please try again.' });
                        } finally {
                          setIsExporting(false);
                        }
                      }}
                      disabled={isExporting}
                      className="px-4 py-2 rounded-lg border font-medium text-sm transition-all hover:bg-gray-50 disabled:opacity-50"
                      style={{ borderColor: 'var(--twc-timberwolf)', color: 'var(--twc-feldgrau)' }}
                    >
                      {isExporting ? 'Exporting...' : 'Export Data'}
                    </button>
                  </div>

                  {/* Delete Account */}
                  <div className="flex items-center justify-between py-4">
                    <div className="flex items-center gap-3">
                      <Trash2 className="w-5 h-5 text-red-500" />
                      <div>
                        <p className="font-medium text-red-600">Delete Account</p>
                        <p className="text-sm text-gray-500">Permanently delete your account and all data</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowDeleteDialog(true)}
                      className="px-4 py-2 rounded-lg bg-red-600 text-white font-medium text-sm transition-all hover:bg-red-700"
                    >
                      Delete Account
                    </button>
                  </div>
                </div>

                {/* Delete Account Dialog */}
                {showDeleteDialog && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
                      <h3 className="text-lg font-semibold text-red-600 mb-4">Delete Your Account</h3>
                      <p className="text-gray-600 text-sm mb-4">
                        This action is irreversible. All your data including profile, applications,
                        saved jobs, messages, and uploaded files will be permanently deleted.
                      </p>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Enter your password to confirm
                        </label>
                        <input
                          type="password"
                          value={deletePassword}
                          onChange={(e) => setDeletePassword(e.target.value)}
                          className="w-full p-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                          placeholder="Your password"
                        />
                      </div>
                      <div className="flex gap-3 justify-end">
                        <button
                          onClick={() => { setShowDeleteDialog(false); setDeletePassword(''); }}
                          className="px-4 py-2 rounded-lg border font-medium text-sm"
                          style={{ borderColor: 'var(--twc-timberwolf)', color: 'var(--twc-feldgrau)' }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={async () => {
                            if (!deletePassword) {
                              addToast({ type: 'error', title: 'Password Required', message: 'Please enter your password to confirm.' });
                              return;
                            }
                            try {
                              setIsDeleting(true);
                              await api.delete('/auth/account', { password: deletePassword });
                              addToast({ type: 'success', title: 'Account Deleted', message: 'Your account has been permanently deleted.' });
                              await logout();
                              navigate(ROUTE_NAMES.PAGES.HOME);
                            } catch (err: any) {
                              const message = err?.message || 'Failed to delete account.';
                              addToast({ type: 'error', title: 'Deletion Failed', message });
                            } finally {
                              setIsDeleting(false);
                              setShowDeleteDialog(false);
                              setDeletePassword('');
                            }
                          }}
                          disabled={isDeleting || !deletePassword}
                          className="px-4 py-2 rounded-lg bg-red-600 text-white font-medium text-sm hover:bg-red-700 disabled:opacity-50"
                        >
                          {isDeleting ? 'Deleting...' : 'Permanently Delete'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfilePage;
